import * as React from 'react';
import * as addDays from 'date-fns/add_days';
import * as parse from 'date-fns/parse';

import { EntityActions, EntityType } from 'common/entity';

import { MemberLink } from 'common/components/link/MemberLink';
import { AppState } from 'common/models/AppState';
import { Datetime } from 'common/components/Datetime';
import { InfiniteList } from 'common/components/InfiniteList';
import { Link } from 'react-router-dom';
import { Loading } from 'common/components/Loading';
import { Money } from 'common/components/Money';
import { Table } from 'reactstrap';
import { Transaction } from 'common/api/models/Transaction';
import { TransactionApi } from 'common/api/resources/TransactionApi';
import { toApiError } from 'common/utils/api';

interface TransactionsTableProps {
  appState: AppState;
  entityActions: EntityActions;
  search: string;
}

interface TransactionsTableState {
  transactions: Transaction[];
  next?: string;
  isLoading: boolean;
  error?: string;
}

export class TransactionsTable extends React.Component<TransactionsTableProps, TransactionsTableState> {
  state: TransactionsTableState = { transactions: [], isLoading: true };

  componentDidMount() {
    this.reload(this.props.search);
  }

  componentDidUpdate(prevProps: TransactionsTableProps) {
    if (prevProps.search !== this.props.search) {
      this.reload(this.props.search);
    }
  }

  handleLoadMore = () => {
    this.reload(this.props.search, this.state.next);
  };

  reload(search: string, next?: string) {
    this.setState({ isLoading: true });

    let num;
    let fromAt: number | undefined = undefined;
    if (search) {
      const searchTerms = search.trim().split(' ');
      for (let term of searchTerms) {
        if (term.startsWith('date:')) {
          fromAt = addDays(parse(term.substring(5)), 1).getTime();
        } else {
          num = Number(term);
        }
      }
    }

    if (num) {
      TransactionApi.getTransactionByNumber(num).then(
        res => {
          this.setState({
            transactions: [res],
            isLoading: false,
            error: undefined,
          });
          this.props.entityActions.fetch(EntityType.MEMBER, res.createId!);
          this.props.entityActions.fetch(EntityType.MEMBER, res.accountId!);
        },
        err => {
          this.setState({
            transactions: [],
            error: toApiError(err),
            isLoading: false,
          });
        }
      );
    } else {
      TransactionApi.getTransactions({ from: next, fromAt }).then(
        res => {
          this.setState({
            transactions: next ? [...this.state.transactions, ...res.data] : res.data,
            next: res.next,
            isLoading: false,
            error: undefined,
          });
          const memberIds = res.data.map(t => t.createId!).concat(res.data.map(t => t.accountId!));
          this.props.entityActions.fetchMissingIds(EntityType.MEMBER, memberIds);
        },
        err => {
          this.setState({ error: toApiError(err), isLoading: false });
        }
      );
    }
  }

  render() {
    const cols = 9;
    const accounts = this.props.appState.entities.member;
    if (this.state.error) {
      return (
        <div className="alert alert-danger" style={{ marginTop: 20 }}>
          {this.state.error}
        </div>
      );
    }
    return (
      <InfiniteList hasMore={!!this.state.next} isLoading={this.state.isLoading} onLoadMore={this.handleLoadMore}>
        <Table responsive={true} hover={true} striped={true}>
          <thead>
            <tr>
              <th>Num</th>
              <th>Account</th>
              <th>Amount</th>
              <th>Type</th>
              <th>Details</th>
              <th>Order</th>
              <th>Date</th>
              <th>Created</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {!this.state.isLoading &&
              this.state.transactions.length === 0 && (
                <tr>
                  <td colSpan={cols}>No results found.</td>
                </tr>
              )}
            {this.state.transactions.map(t => (
              <tr key={t.id}>
                <td>{t.number}</td>
                <td>
                  <MemberLink member={accounts[t.accountId!]} />
                </td>
                <td>
                  <Money value={t.amount!} />
                </td>
                <td>
                  {t.type === Transaction.Type.CHECK && <span>Check</span>}
                  {t.type === Transaction.Type.CHARGE && <span>Charge</span>}
                  {t.type === Transaction.Type.WIRE && <span>Wire</span>}
                  {t.type === Transaction.Type.RECEIVABLE && <span>Receivable</span>}
                  {t.type === Transaction.Type.REFUND_CHARGE && <span>Refund (Charge)</span>}
                  {t.type === Transaction.Type.REFUND_CHECK && <span>Refund (Check)</span>}
                  {t.type === Transaction.Type.CANCEL && <span>Cancellation</span>}
                  {t.type === Transaction.Type.ADJUSTMENT && <span>Adjustment</span>}
                </td>
                <td>
                  <small>
                    {t.checkNumber} {t.stripeChargeId} {t.type === Transaction.Type.ADJUSTMENT && t.description}
                  </small>
                </td>
                <td>{t.orderNumber && <Link to={'/orders/' + t.orderNumber}>{t.orderNumber}</Link>}</td>
                <td>{t.date}</td>
                <td>
                  <Datetime at={t.at} />
                </td>
                <td>
                  <MemberLink member={this.props.appState.entities.member[t.createId!]} />
                </td>
              </tr>
            ))}
            {this.state.isLoading && (
              <tr>
                <td colSpan={cols}>
                  <Loading />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </InfiniteList>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Transactions/TransactionsTable.tsx